import {
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  Analysis,
  FilterDates,
  FilterState,
  FilterUser,
  Folder,
  FolderHistory,
  HistoryRecord,
  FolderResult,
} from "../interfaces/folder.types.ts";
import {
  addCjaNotesToFolder,
  addNotesToFolder,
  createFolder,
  fetchAllFolders,
  fetchFolderById,
  fetchFolderHistory,
  fetchMyFolders,
  onRemoveFolderTemplate,
  onUpdateAnalysisForm,
  savePdfUrl,
  toggleFolderActive,
  updateFolder,
} from "../actions/folderActions.tsx";
import { addTemplatesToFolder } from "../actions/templatesActions.tsx";

export interface FoldersState extends FolderResult {
  loading: boolean;
  loadingStatus: boolean;
  loadingAnalysis: boolean;
  activeFolder: Folder | null;
  activeFolderHistory: HistoryRecord[] | FolderHistory | null; // Updated to handle both formats

  filtered: Folder[];
  filterDates: FilterDates | null;
  filterChoice: string;

  sorted: Folder[];
  sortingChoice: string;

  filterUser: FilterUser | null;
  pdfUrls: { [key: number]: string };
}

const initialState = {
  count: 0,
  next: null,
  previous: null,
  results: [],
  loading: false,
  loadingStatus: false,
  loadingAnalysis: false,
  activeFolder: null,
  activeFolderHistory: null,
  filtered: [],
  filterDates: null,
  filterChoice: "all",
  sorted: [],
  sortingChoice: "recent",
  filterUser: null,
  pdfUrls: {},
} satisfies FoldersState as FoldersState;

const folderSlice = createSlice({
  name: "folders",
  initialState,
  reducers: {
    setActiveFolder: (state, action: PayloadAction<Folder>) => {
      state.activeFolder = action.payload;
    },
    clearActiveFolder: (state) => {
      state.activeFolder = null;
    },
    setFiltered: (state, action: PayloadAction<Folder[]>) => {
      state.filtered = action.payload;
    },
    setFilterChoice: (state, action: PayloadAction<string>) => {
      state.filterChoice = action.payload;
    },
    setFilterDates: (state, action: PayloadAction<FilterDates>) => {
      state.filterDates = action.payload;
    },
    clearFilterDates: (state) => {
      state.filterDates = null;
    },
    setSortingChoice: (state, action: PayloadAction<string>) => {
      state.sortingChoice = action.payload;
    },
    setFilterUser: (state, action: PayloadAction<FilterUser>) => {
      state.filterUser = action.payload;
    },
    clearFilterUsers: (state) => {
      state.filterUser = null;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setFilterState: (state, action: PayloadAction<FilterState>) => {
      state.count = action.payload.count;
      state.next = action.payload.next;
      state.previous = action.payload.previous;
      state.results = action.payload.results;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<FoldersState>) => {
    builder
      .addCase(fetchAllFolders.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchAllFolders.fulfilled,
        (state, action: PayloadAction<FolderResult>) => {
          const existingIds = new Set(state.results.map((folder) => folder.id));

          state.results = [
            ...state.results,
            ...action.payload.results.filter((folder) => {
              if (!existingIds.has(folder.id)) {
                existingIds.add(folder.id);
                return true;
              }
              return false;
            }),
          ];

          state.count = action.payload.count;
          state.next = action.payload.next;
          state.previous = action.payload.previous;
          state.loading = false;
        },
      )
      .addCase(fetchAllFolders.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(createFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        createFolder.fulfilled,
        (state, action: PayloadAction<Folder>) => {
          state.results.push(action.payload);
          state.loading = false;
        },
      )
      .addCase(createFolder.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(updateFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        updateFolder.fulfilled,
        (state, action: PayloadAction<Folder>) => {
          // Find and update the folder in the results array
          const index = state.results.findIndex(
            (folder) => folder.id === action.payload.id,
          );
          if (index !== -1) {
            state.results[index] = action.payload;
          }
          state.loading = false;
        },
      )
      .addCase(updateFolder.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(addNotesToFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(addNotesToFolder.fulfilled, (state, action: PayloadAction<Folder>) => {
        // Update the folder in the results array
        const index = state.results.findIndex(
          (folder) => folder.id === action.payload.id,
        );
        if (index !== -1) {
          state.results[index] = action.payload;
        }

        // Update activeFolder if it's the same folder
        if (state.activeFolder && state.activeFolder.id === action.payload.id) {
          state.activeFolder = action.payload;
        }

        state.loading = false;
      })
      .addCase(addNotesToFolder.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(addCjaNotesToFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(addCjaNotesToFolder.fulfilled, (state, action: PayloadAction<Folder>) => {
        // Update the folder in the results array
        const index = state.results.findIndex(
          (folder) => folder.id === action.payload.id,
        );
        if (index !== -1) {
          state.results[index] = action.payload;
        }

        // Update activeFolder if it's the same folder
        if (state.activeFolder && state.activeFolder.id === action.payload.id) {
          state.activeFolder = action.payload;
        }

        state.loading = false;
      })
      .addCase(addCjaNotesToFolder.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(toggleFolderActive.pending, (state) => {
        state.loadingStatus = true;
      })
      .addCase(toggleFolderActive.fulfilled, (state) => {
        state.loadingStatus = false;
      })
      .addCase(toggleFolderActive.rejected, (state) => {
        state.loadingStatus = false;
      });

    builder
      .addCase(fetchFolderById.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchFolderById.fulfilled,
        (state, action: PayloadAction<Folder>) => {
          state.activeFolder = action.payload;
          state.loading = false;

          // Check if the folder exists in results
          const exists = state.results.some(
            (folder) => folder.id === action.payload.id,
          );

          if (exists) {
            // Update existing folder
            state.results = state.results.map((folder) =>
              folder.id === action.payload.id ? action.payload : folder,
            );
          } else {
            // Add the folder if it doesn't exist
            state.results.push(action.payload);
          }
        },
      )
      .addCase(fetchFolderById.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(fetchFolderHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchFolderHistory.fulfilled,
        (state, action: PayloadAction<HistoryRecord[] | FolderHistory>) => {
          // Handle both the new and old response formats
          state.activeFolderHistory = action.payload;
          state.loading = false;
        },
      )
      .addCase(fetchFolderHistory.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(onUpdateAnalysisForm.pending, (state) => {
        state.loadingAnalysis = true;
      })
      .addCase(
        onUpdateAnalysisForm.fulfilled,
        (state, action: PayloadAction<Analysis>) => {
          state.activeFolder!.analysis = action.payload;
          state.loadingAnalysis = false;
        },
      )
      .addCase(onUpdateAnalysisForm.rejected, (state) => {
        state.loadingAnalysis = false;
      });

    builder
      .addCase(onRemoveFolderTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        onRemoveFolderTemplate.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          if (state.activeFolder) {
            state.activeFolder.templates = state.activeFolder.templates?.filter(
              (template) => template.slug !== action.payload,
            );
          }
          const activeFolderId = state.activeFolder?.id;
          if (activeFolderId) {
            const folderIndex = state.results.findIndex(
              (folder) => folder.id === activeFolderId,
            );
            if (folderIndex !== -1) {
              state.results[folderIndex].templates = state.results[
                folderIndex
              ].templates?.filter(
                (template) => template.slug !== action.payload,
              );
            }
          }
        },
      )
      .addCase(onRemoveFolderTemplate.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(addTemplatesToFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        addTemplatesToFolder.fulfilled,
        (state, action: PayloadAction<Folder>) => {
          state.activeFolder = action.payload;
          state.loading = false;

          // Check if the folder exists in results
          const exists = state.results.some(
            (folder) => folder.id === action.payload.id,
          );

          if (exists) {
            // Update existing folder
            state.results = state.results.map((folder) =>
              folder.id === action.payload.id ? action.payload : folder,
            );
          } else {
            // Add the folder if it doesn't exist
            state.results.push(action.payload);
          }
        },
      )
      .addCase(addTemplatesToFolder.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(
        savePdfUrl.fulfilled,
        (
          state,
          action: PayloadAction<{ folderNumber: number; url: string }>,
        ) => {
          state.pdfUrls[action.payload.folderNumber] = action.payload.url;
        },
      )
      .addCase(fetchMyFolders.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchMyFolders.fulfilled,
        (state, action: PayloadAction<FolderResult>) => {
          // Replace results on first page, append on pagination
          if (action.payload.previous === null) {
            // First page load or filter change
            state.results = action.payload.results;
          } else {
            // Pagination - append results
            const existingIds = new Set(
              state.results.map((folder) => folder.id),
            );

            state.results = [
              ...state.results,
              ...action.payload.results.filter(
                (folder) => !existingIds.has(folder.id),
              ),
            ];
          }

          state.count = action.payload.count;
          state.next = action.payload.next;
          state.previous = action.payload.previous;
          state.loading = false;
        },
      )
      .addCase(fetchMyFolders.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setActiveFolder,
  clearActiveFolder,
  setFiltered,
  setFilterChoice,
  setFilterDates,
  clearFilterDates,
  setSortingChoice,
  setFilterUser,
  clearFilterUsers,
  setIsLoading,
  setFilterState,
} = folderSlice.actions;
export default folderSlice.reducer;
